import { Box, Divider, FormHelperText, Typography } from "@mui/material"
import type { FC } from "react"
import { useCallback } from "react"

import type { PayableApprovalsSearchParams } from "../../types/searchParams"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosDatePicker } from "@/components/UiKit"
import { useDraftDateRange } from "@/hooks/useDraftDateRange"

interface PayableApprovalsFilterDrawerProps {
  open: boolean
  onClose: () => void
  searchParams: PayableApprovalsSearchParams
  onChange: <K extends keyof PayableApprovalsSearchParams>(key: K, value: PayableApprovalsSearchParams[K]) => void
  onBatchChange: (updates: Partial<PayableApprovalsSearchParams>) => void
  onClear: () => void
}

export const PayableApprovalsFilterDrawer: FC<PayableApprovalsFilterDrawerProps> = ({
  open,
  onClose,
  searchParams,
  onBatchChange,
  onClear,
}) => {
  const { dueDateFrom, dueDateTo } = searchParams

  const handleRangeChange = useCallback(
    (range: { from: string | undefined; to: string | undefined }) =>
      onBatchChange({ dueDateFrom: range.from, dueDateTo: range.to }),
    [onBatchChange]
  )

  const { fromDraft, toDraft, isRangeInvalid, handleFromChange, handleToChange } = useDraftDateRange({
    from: dueDateFrom,
    to: dueDateTo,
    onRangeChange: handleRangeChange,
  })

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onClose} clear={onClear} testId='payable-approvals.filter-drawer'>
      <Box className='h-full min-w-80'>
        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Due Date Range
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
            <Box className='flex flex-col gap-4 py-4'>
              <AerosDatePicker
                label='From'
                value={fromDraft ?? null}
                onChange={handleFromChange}
                error={isRangeInvalid}
                data-testid='payable-approvals.filter-drawer.due-date-from-field'
              />
              <AerosDatePicker
                label='To'
                value={toDraft ?? null}
                onChange={handleToChange}
                error={isRangeInvalid}
                data-testid='payable-approvals.filter-drawer.due-date-to-field'
              />
              {isRangeInvalid && <FormHelperText error>From date must be on or before To date</FormHelperText>}
            </Box>
          </Box>
        </Box>
      </Box>
    </SearchFilterDrawer>
  )
}
